import React, { createContext, useContext, useState } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { useSidebarState } from '../hooks/useSidebarState';

/**
 * Context for managing sidebar state across the application
 */
const SidebarContext = createContext(undefined);

/**
 * Custom hook to access sidebar context
 * @throws {Error} If used outside of SidebarProvider
 * @returns {Object} { isOpen: boolean, toggleSidebar: function, isMobile: boolean }
 */
export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within SidebarProvider');
  }
  return context;
};

/**
 * Provider component for sidebar state management
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 */
export const SidebarProvider = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Use custom hook for persistent state
  const { isOpen, setIsOpen } = useSidebarState();
  
  // Mobile drawer state (separate from desktop collapse state)
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  /**
   * Toggle sidebar state
   * On desktop: toggles between expanded/collapsed
   * On mobile: toggles mobile drawer
   */
  const toggleSidebar = () => {
    if (isMobile) {
      setMobileDrawerOpen(prev => !prev);
    } else {
      setIsOpen(prev => !prev);
    }
  };

  /**
   * Close mobile drawer (used when navigating)
   */
  const closeMobileDrawer = () => {
    setMobileDrawerOpen(false);
  };

  const value = {
    isOpen: isMobile ? true : isOpen, // Always "open" on mobile (drawer overlay)
    toggleSidebar,
    isMobile,
    mobileDrawerOpen,
    closeMobileDrawer,
  };

  return (
    <SidebarContext.Provider value={value}>
      {children}
    </SidebarContext.Provider>
  );
};

export default SidebarContext;
